import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { UserModel, UserService } from '../../services/user.service';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { OciService } from '../../services/oci.service';

@Component({
  selector: 'app-profile',
  imports: [ReactiveFormsModule, AvatarModule, InputTextModule, Toast],
  templateUrl: './profile.html',
  styleUrls: ['./profile.css'],
  providers: [MessageService]
})
export class Profile implements OnDestroy {
  user: UserModel | null = null;
  userId: number = 0;
  userTypeId: number = 0;
  roleId: number = 0;
  isSaving = false;
  isUploadingPhoto = false;
  profileForm!: FormGroup;
  photoUrl: string | null = null;

  private _photoObjectUrl: string | null = null;

  constructor(
    private userService: UserService,
    public config: DynamicDialogConfig,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private messageService: MessageService,
    private ociService: OciService
  ) {}

  ngOnInit() {
    this.userId     = this.config.data?.userId     || 0;
    this.userTypeId = this.config.data?.userTypeId || 0;
    this.roleId     = this.config.data?.roleId     || 0;
    this.profileForm = this.fb.group({
      // Read-only — always disabled
      email:        [{ value: '', disabled: true }],
      roleName:     [{ value: '', disabled: true }],
      userTypeName: [{ value: '', disabled: true }],
      status:       [{ value: '', disabled: true }],
      // Editable
      firstName:   ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      lastName:    ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      phoneNumber: ['', [Validators.required, Validators.maxLength(20)]],
      address1:    ['', [Validators.maxLength(200)]],
      address2:    ['', [Validators.maxLength(200)]],
      city:        ['', [Validators.required, Validators.maxLength(100)]],
      state:       ['', [Validators.required, Validators.maxLength(100)]],
      zip:         ['', [Validators.maxLength(20)]],
      country:     ['', [Validators.maxLength(100)]]
    });

    this.userService.getUserById(this.userId).subscribe(user => {
      this.user = user;
      this.profileForm.patchValue({
        email:        user.email,
        roleName:     user.roleName,
        userTypeName: user.userTypeName || '',
        status:       user.isActive ? 'Active' : 'Inactive',
        firstName:    user.firstName,
        lastName:     user.lastName,
        phoneNumber:  user.phoneNumber || '',
        address1:     user.address1 || '',
        address2:     user.address2 || '',
        city:         user.city || '',
        state:        user.state || '',
        zip:          user.zip || '',
        country:      user.country || ''
      });
      if (user.photoPath) {
        this.loadPhoto(user.photoPath);
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this._photoObjectUrl) {
      URL.revokeObjectURL(this._photoObjectUrl);
    }
  }

  private loadPhoto(path: string): void {
    this.ociService.getOciResourceEnhancedWithoutSpinner(path).subscribe({
      next: (blob) => {
        if (this._photoObjectUrl) {
          URL.revokeObjectURL(this._photoObjectUrl);
        }
        this._photoObjectUrl = URL.createObjectURL(blob);
        this.photoUrl = this._photoObjectUrl;
        this.cdr.markForCheck();
      },
      error: () => {
        this.photoUrl = null;
        this.cdr.markForCheck();
      }
    });
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Only JPG, PNG, GIF, WEBP are allowed.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({ severity: 'error', summary: 'File Too Large', detail: 'Maximum file size is 5MB.' });
      return;
    }

    this.isUploadingPhoto = true;
    this.userService.uploadProfilePicture(this.userId, file).subscribe({
      next: (res) => {
        this.isUploadingPhoto = false;
        if (this.user) this.user.photoPath = res.photoPath;
        this.loadPhoto(res.photoPath);
        this.messageService.add({ severity: 'success', summary: 'Photo Updated', detail: 'Profile picture updated successfully.' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.isUploadingPhoto = false;
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload profile picture.' });
        this.cdr.markForCheck();
      }
    });

    // Reset so the same file can be re-selected
    input.value = '';
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    const payload: Partial<UserModel> = {
      // Required by backend — email/status from loaded user, ids from SSO session
      email:       this.user!.email,
      roleId:      this.roleId,
      userTypeId:  this.userTypeId,
      isActive:    this.user!.isActive,
      // Editable fields
      firstName:   this.profileForm.get('firstName')?.value,
      lastName:    this.profileForm.get('lastName')?.value,
      phoneNumber: this.profileForm.get('phoneNumber')?.value || '',
      address1:    this.profileForm.get('address1')?.value,
      address2:    this.profileForm.get('address2')?.value,
      city:        this.profileForm.get('city')?.value,
      state:       this.profileForm.get('state')?.value,
      zip:         this.profileForm.get('zip')?.value,
      country:     this.profileForm.get('country')?.value
    };
    this.userService.editUser(this.userId, payload).subscribe({
      next: () => {
        this.user = { ...this.user!, ...payload };
        this.isSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Profile updated successfully' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update profile' });
        this.cdr.markForCheck();
      }
    });
  }

  get initials(): string {
    const first = this.user?.firstName?.charAt(0)?.toUpperCase() ?? '';
    const last  = this.user?.lastName?.charAt(0)?.toUpperCase() ?? '';
    return first + last;
  }

  get f() { return this.profileForm.controls; }
}
